import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';

interface UpdateSortOrderInput {
  itemId: string;
  updates: Array<{ id: string; sort_order: number }>;
}

export const useUpdateMilestoneSortOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, updates }: UpdateSortOrderInput) => {
      logger.debug('useUpdateMilestoneSortOrder', 'Updating sort order', { itemId, updates });

      const TEMP_OFFSET = 10000; // Temporary offset to avoid conflicts

      try {
        // Phase 1: Bump all sort_orders to temporary values to avoid unique constraint violations
        logger.debug('useUpdateMilestoneSortOrder', 'Phase 1: Temporary bump', { count: updates.length });
        for (const { id, sort_order } of updates) {
          const { error } = await supabase
            .from('item_milestones')
            .update({ sort_order: sort_order + TEMP_OFFSET })
            .eq('id', id)
            .select();

          if (error) {
            logger.error('useUpdateMilestoneSortOrder', 'Phase 1 failed for milestone', {
              milestoneId: id,
              tempSortOrder: sort_order + TEMP_OFFSET,
              error
            });
            throw error;
          }
        }

        // Phase 2: Set final sort_order values
        logger.debug('useUpdateMilestoneSortOrder', 'Phase 2: Final values', { count: updates.length });
        for (const { id, sort_order } of updates) {
          const { error } = await supabase
            .from('item_milestones')
            .update({ sort_order })
            .eq('id', id)
            .select();

          if (error) {
            logger.error('useUpdateMilestoneSortOrder', 'Phase 2 failed for milestone', {
              milestoneId: id,
              finalSortOrder: sort_order,
              error
            });
            throw error;
          }
        }

        logger.info('useUpdateMilestoneSortOrder', 'Sort order updated successfully', { 
          itemId, 
          count: updates.length 
        });
        
        return { itemId };
      } catch (error: any) {
        logger.error('useUpdateMilestoneSortOrder', 'Update failed', { 
          itemId, 
          error: error.message || error 
        });
        throw new Error(error.message || 'Failed to update milestone order');
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item-milestones', data.itemId] });
      queryClient.invalidateQueries({ queryKey: ['items-milestones'] });
      toast({
        title: "Thành công",
        description: "Thứ tự milestones đã được cập nhật",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Lỗi",
        description: error.message || "Không thể cập nhật thứ tự",
        variant: "destructive",
      });
    },
  });
};
